import React, { useCallback, useEffect, useState } from 'react';
import {
    DeviceExtValue,
    SchemaGroup,
    SchemaGroupField,
    deviceSchemaClient,
    groupAppliesToDevice,
    listDeviceExtValues,
    parseGroupFields,
    upsertDeviceExtValue,
} from '../../api/schema';
import HelpTip from '../../components/HelpTip';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import MatrixGroupEditor, { loadMatrixRows, MatrixParams, MatrixRow } from '../../components/MatrixGroupEditor';
import {
    SchemaFieldInput,
    buildExtPatch,
    extValueToInput,
    saveExtPatch,
    schemaFieldText,
    useDictOptions,
    withListTypes,
} from './deviceSchemaForm';
import { alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import GroupedFields from '../../components/GroupedFields';
import { parseLayout } from '../../utils/fieldGroups';
import LayoutMatrixGroup from './LayoutMatrixGroup';

const rowsKey = (g: SchemaGroup) => g.rowSource || '';
const fieldAttrs = (f: SchemaGroupField) => f;
const fieldKeyOf = (f: SchemaGroupField) => f.key;

// 设备动态字段组面板 (Schema 驱动): 设备工况 Profile 页与 设备管理「字段」弹窗共用.
// 组定义在「Schema 管理 → 模板 → 设备」维护; 平铺组行内编辑, 矩阵组复用 MatrixGroupEditor.
// 全部字段统一读写 device_ext_value (2026-08-14 store=column 机制移除) —
// 与 DeviceCapabilityCard / 编辑设备弹窗 同一规则, 保证各编辑入口行为一致.
// ext 值按 (deviceKey, groupKey) 整组覆盖保存, admin 即时生效, 普通用户走审批工单.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

function DeviceSchemaGroups({
    deviceKey,
    only,
    exclude,
    archCode,
    layoutFilter,
    hideEmpty,
    title,
    wrapClassName,
    groups: groupsProp,
    extValues: extValuesProp,
    onReload,
}: {
    deviceKey: string;
    /** 只渲染这些组 (如工况页模式能力卡片) */
    only?: string[];
    /** 排除这些组 (如已由专属卡片渲染的 capability/mode_capability) */
    exclude?: string[];
    /** 设备所属工艺框架: 过滤组作用域 (组 archCode 空=通用 或 命中) */
    archCode?: string;
    /** 只渲染某种布局 (flat / matrix) */
    layoutFilter?: string;
    /** 无匹配组时整体不渲染 (含加载中), 用于可有可无的区块 (如工况页自定义组卡片) */
    hideEmpty?: boolean;
    /** 卡片标题: 提供时自带标题头, 配合 wrapClassName 形成独立卡片 */
    title?: string;
    /** 外层容器 class (如 card p-4 col-span-3); 仅在有内容可渲染时输出 */
    wrapClassName?: string;
    /** 父级已取到的组定义 (设备详情随行下发 detail.schemaGroups); 与 extValues 成对给, 给了就不再自取 */
    groups?: SchemaGroup[];
    /** 父级已取到的 ext 值 (detail.extValues) */
    extValues?: DeviceExtValue[];
    /** 父级供数时的刷新回调: 保存后由父级重取详情 (组件内不再自己拉, 否则拿到的还是旧 props) */
    onReload?: () => void | Promise<void>;
}) {
    const canUpdate = useCan('device', 'update');
    const [groups, setGroups] = useState<SchemaGroup[]>([]);
    // 平铺组编辑值 {groupKey: {fieldKey: str}}; 矩阵组原始嵌套值 {groupKey: {row: {field: value}}}
    const [values, setValues] = useState<Record<string, Record<string, string>>>({});
    const [matrixVals, setMatrixVals] = useState<Record<string, MatrixParams>>({});
    // 平铺组原始值 (未转表单串): 推断列表字段用 (字段组定义没写 type 但存量是数组)
    const [rawVals, setRawVals] = useState<Record<string, Record<string, unknown>>>({});
    const [matrixRows, setMatrixRows] = useState<Record<string, MatrixRow[]>>({});
    const [loading, setLoading] = useState(true);
    const [editingGroup, setEditingGroup] = useState('');
    const [savingGroup, setSavingGroup] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        // 组定义 + ext 值优先吃父级下发的详情数据 (工况页三个卡片共用一份, 见 DeviceDetail.schemaGroups);
        // 独立入口 (设备管理「字段」弹窗) 没有父级数据时自取.
        const [gs, vals] =
            groupsProp && extValuesProp
                ? [groupsProp, extValuesProp]
                : await Promise.all([deviceSchemaClient.list(), listDeviceExtValues(deviceKey)]);
        let enabled = gs.filter((g) => g.isEnabled);
        if (only) enabled = enabled.filter((g) => only.includes(g.groupKey));
        if (exclude) enabled = enabled.filter((g) => !exclude.includes(g.groupKey));
        // Schema 显式绑定: 设备绑定其框架模板, 只渲染模板自含的组 (无通用组叠加);
        // 加工类型维度不过滤 (设备覆盖其全部类别)
        if (archCode !== undefined) enabled = enabled.filter((g) => g.archCode === archCode);
        // 组内设备绑定: 组只对部分设备开放时, 未绑定的设备不显示该组 (与后端读写过滤同口径)
        enabled = enabled.filter((g) => groupAppliesToDevice(g, deviceKey));
        if (layoutFilter) enabled = enabled.filter((g) => (g.layout || 'flat') === layoutFilter);
        setGroups(enabled);
        const byGroup: Record<string, Record<string, string>> = {};
        const byGroupRaw: Record<string, MatrixParams> = {};
        const byGroupObj: Record<string, Record<string, unknown>> = {};
        vals.forEach((v: DeviceExtValue) => {
            try {
                const obj = JSON.parse(v.params) as Record<string, unknown>;
                byGroupRaw[v.groupKey] = obj as MatrixParams;
                byGroupObj[v.groupKey] = obj;
                byGroup[v.groupKey] = Object.fromEntries(
                    Object.entries(obj)
                        // 数组 (列表字段) 要保留: 整组覆盖保存时漏掉就等于删掉存量值
                        .filter(([, val]) => val == null || typeof val !== 'object' || Array.isArray(val))
                        .map(([k, val]) => [k, extValueToInput(val)]),
                );
            } catch {
                byGroup[v.groupKey] = {};
            }
        });
        setValues(byGroup);
        setMatrixVals(byGroupRaw);
        setRawVals(byGroupObj);
        const seen = new Set<string>();
        enabled
            .filter((g) => g.layout === 'matrix' && g.rowSource)
            .forEach((g) => {
                const k = rowsKey(g);
                if (seen.has(k)) return;
                seen.add(k);
                loadMatrixRows(g.rowSource as string)
                    .then((rows) => setMatrixRows((prev) => ({ ...prev, [k]: rows })))
                    .catch(() => {});
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceKey, (only || []).join(','), (exclude || []).join(','), archCode, layoutFilter, groupsProp, extValuesProp]);

    // 保存后的刷新: 父级供数时要让父级重取详情 —— 自己再 load 只会把旧 props 读回来.
    const refresh = useCallback(async () => {
        if (onReload) await onReload();
        else await load();
    }, [onReload, load]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setEditingGroup('');
        (async () => {
            try {
                await load();
            } catch (e) {
                if (!cancelled) setError(errMsg(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [load]);

    const patch = (groupKey: string, fieldKey: string, v: string) =>
        setValues((prev) => ({ ...prev, [groupKey]: { ...(prev[groupKey] || {}), [fieldKey]: v } }));

    const dictOpts = useDictOptions(
        groups.flatMap((g) => parseGroupFields(g.fields).map((f) => f.optionsSource)),
    );

    const saveFlat = async (g: SchemaGroup) => {
        // 空串字段不写入 (与理化录入一致: 未填 = 不设置, 整组覆盖时该键即被移除); 枚举字段存字符串, 其余必须是数字
        const fields = withListTypes(parseGroupFields(g.fields), rawVals[g.groupKey]);
        const { extPatch, invalid } = buildExtPatch(fields, values[g.groupKey] || {});
        if (invalid) {
            alertDialog(`「${invalid.field.label}」不是有效数字: ${invalid.raw}`);
            return;
        }
        setSavingGroup(g.groupKey);
        try {
            const message = await saveExtPatch(deviceKey, g.groupKey, extPatch);
            await refresh();
            setEditingGroup('');
            alertApprovalIfPending(message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setSavingGroup('');
        }
    };

    const wrap = (children: React.ReactNode) =>
        title || wrapClassName ? (
            <div className={wrapClassName}>
                {title && (
                    <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                        {title}
                        <span className="tag bg-brand-50 text-brand-700 font-normal text-[10px]">Schema 驱动</span>
                    </div>
                )}
                {children}
            </div>
        ) : (
            <>{children}</>
        );

    if (loading) {
        if (hideEmpty) return null;
        return wrap(<div className="text-center text-slate-400 text-sm py-6">加载中…</div>);
    }
    if (error) return wrap(<div className="text-sm text-rose-600">错误: {error}</div>);
    if (groups.length === 0) {
        if (hideEmpty) return null;
        return wrap(
            <div className="text-center text-slate-400 text-xs py-6">
                暂无启用的设备字段组。先去「Schema 管理 → 模板 → 设备」新建。
            </div>,
        );
    }

    return wrap(
        <div className="space-y-5">
            {groups.map((g) => {
                const fs = withListTypes(parseGroupFields(g.fields), rawVals[g.groupKey]);
                const fieldLayout = parseLayout(g.fieldLayout);
                if (g.layout === 'matrix' && fieldLayout) {
                    return (
                        <LayoutMatrixGroup
                            key={g.groupKey}
                            deviceKey={deviceKey}
                            group={g}
                            fields={fs}
                            layout={fieldLayout}
                            rows={matrixRows[rowsKey(g)] || []}
                            params={matrixVals[g.groupKey] || null}
                            dictOpts={dictOpts}
                            canUpdate={canUpdate}
                            onSaved={refresh}
                        />
                    );
                }
                if (g.layout === 'matrix') {
                    return (
                        <MatrixGroupEditor
                            resource="device"
                            key={g.groupKey}
                            label={g.label}
                            src={g.src}
                            rowLabel={g.rowLabel}
                            rowSource={g.rowSource}
                            fields={fs}
                            rows={matrixRows[rowsKey(g)] || []}
                            params={matrixVals[g.groupKey] || null}
                            onSave={async (out) => {
                                const res = await upsertDeviceExtValue(deviceKey, g.groupKey, JSON.stringify(out));
                                return res.message;
                            }}
                            onSaved={refresh}
                        />
                    );
                }
                const editing = editingGroup === g.groupKey;
                return (
                    <div key={g.groupKey}>
                        <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-2">
                            {g.label}
                            {g.src && <HelpTip text={<>数据来源: <code>{g.src}</code></>} />}
                            {!editing && (
                                <button
                                    type="button"
                                    className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                                    onClick={() => setEditingGroup(g.groupKey)} hidden={!canUpdate}>
                                    ✎ 编辑
                                </button>
                            )}
                        </div>
                        {(() => {
                            const renderCell = (f: SchemaGroupField) => {
                                const cur = values[g.groupKey]?.[f.key] ?? '';
                                if (editing) {
                                    return (
                                        <div key={f.key}>
                                            <label className="text-xs text-slate-500 mb-1 block">
                                                {f.label}
                                                {f.unit ? ` (${f.unit})` : ''}
                                            </label>
                                            <SchemaFieldInput
                                                field={f}
                                                value={cur}
                                                onChange={(v) => patch(g.groupKey, f.key, v)}
                                                dictOpts={dictOpts}
                                            />
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={f.key}
                                        className="flex justify-between text-xs border-b border-slate-50 py-1"
                                    >
                                        <span className="text-slate-400">
                                            {f.label}
                                            {f.unit && <span className="text-slate-300"> ({f.unit})</span>}
                                        </span>
                                        <span className={cur === '' ? 'text-slate-300' : 'text-slate-700 font-mono'}>
                                            {cur === '' ? '—' : schemaFieldText(f, rawVals[g.groupKey]?.[f.key] ?? cur, dictOpts)}
                                        </span>
                                    </div>
                                );
                            };
                            // 组有分组布局 (2026-10-23): 页签 → 分组 嵌套, 分组可折叠; 否则原平铺
                            return fieldLayout ? (
                                <GroupedFields
                                    items={fs}
                                    attrs={fieldAttrs}
                                    layout={fieldLayout}
                                    itemKey={fieldKeyOf}
                                    renderItem={renderCell}
                                    gridClassName="grid grid-cols-3 gap-x-4 gap-y-1"
                                    collapsible
                                    showEmpty={editing}
                                />
                            ) : (
                                <div className="grid grid-cols-3 gap-x-4 gap-y-1">{fs.map((f) => <React.Fragment key={f.key}>{renderCell(f)}</React.Fragment>)}</div>
                            );
                        })()}
                        {editing && (
                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn-ghost text-xs"
                                    disabled={savingGroup === g.groupKey}
                                    onClick={() => {
                                        setEditingGroup('');
                                        load();
                                    }}
                                >
                                    取消
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary text-xs"
                                    disabled={savingGroup === g.groupKey}
                                    onClick={() => saveFlat(g)}
                                >
                                    {savingGroup === g.groupKey ? '保存中…' : '保存本组'}
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>,
    );
}

export default DeviceSchemaGroups;
